import React, { useState, useEffect } from "react";
import { Spin, Select, Progress } from "antd";
import { motion } from "framer-motion";
import { ChevronLeft, Users, BarChart2, Zap } from "lucide-react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAdminUsages, getUsageMetricsById } from "@api/user/admin";
import moment from "moment";

const { Option } = Select;

const TeamAnalytics = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [usages, setUsages] = useState();
  const [selectedUsage, setSelectedUsage] = useState();
  const [selectedUsageMetrics, setSelectedUsageMetrics] = useState();

  // Refactored to async/await for readability
  const handleFetchUsages = async () => {
    setLoading(true);
    try {
      const res = await getAdminUsages();
      const formattedData = res.data.map((item) => ({
        ...item,
        formattedDate: `${moment(item.startDate).format("Do MMM")} - ${moment(
          item.endDate
        ).format("Do MMM")} (${moment(item.startDate).format("YYYY")})`,
      }));

      setUsages(formattedData);
      if (formattedData.length > 0) {
        setSelectedUsage(formattedData[0]);
        await handleFetchSelectedUsageMetrics(formattedData[0].usageMetrics);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchSelectedUsageMetrics = async (usageMetricId) => {
    setLoading(true);
    try {
      const res = await getUsageMetricsById(usageMetricId);
      setSelectedUsageMetrics(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUsageChange = async (newValue) => {
    setLoading(true);
    const newSelectedUsage = usages.find(
      (usage) => usage.formattedDate === newValue
    );
    if (newSelectedUsage) {
      setSelectedUsage(newSelectedUsage);
      await handleFetchSelectedUsageMetrics(newSelectedUsage.usageMetrics);
    }
  };

  useEffect(() => {
    handleFetchUsages();
  }, []);

  return (
    <>
      <div
        style={{
          background:
            "linear-gradient(235deg, rgb(184 173 253) 0%, rgb(255 235 255 / 95%) 50%, rgb(219 219 219) 100%)",
          minHeight: "100vh",
          padding: "24px",
          position: "relative",
          overflowY: "auto",
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "calc(100vh - 48px)",
            }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              background: "rgba(255, 255, 255, 0.7)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              padding: "24px",
              minHeight: "calc(100vh - 48px)",
            }}
          >
            {/* Header */}
            <div style={{ marginBottom: "32px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  marginBottom: "24px",
                }}
              >
                <button
                  onClick={() => history.goBack()}
                  style={{
                    background: "rgba(124, 58, 237, 0.1)",
                    border: "none",
                    borderRadius: "12px",
                    padding: "8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ChevronLeft size={20} style={{ color: "#7c3aed" }} />
                </button>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <BarChart2 size={24} style={{ color: "#7c3aed" }} />
                  <h2 style={{ margin: 0, color: "#1e1b4b" }}>
                    {t("teamAnalytics.teamAnalytics")}
                  </h2>
                </div>
              </div>

              <Select
                onChange={handleUsageChange}
                value={selectedUsage?.formattedDate}
                style={{
                  width: "100%",
                  maxWidth: "300px",
                  borderRadius: "12px",
                }}
                dropdownStyle={{
                  borderRadius: "12px",
                }}
              >
                {usages?.map((usage, index) => (
                  <Option key={index} value={usage.formattedDate}>
                    {usage.formattedDate}
                  </Option>
                ))}
              </Select>
            </div>

            {selectedUsageMetrics?.length > 0 && (
              <>
                {/* Plan Stats */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  style={{
                    background: "rgba(255, 255, 255, 0.5)",
                    borderRadius: "16px",
                    padding: "24px",
                    marginBottom: "24px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "16px",
                    }}
                  >
                    <Zap size={24} style={{ color: "#7c3aed" }} />
                    <h3 style={{ margin: 0, color: "#1e1b4b" }}>
                      {
                        selectedUsageMetrics[0].usageMetricsDetails
                          .subscriptionPlan
                      }{" "}
                      Plan
                    </h3>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(250px, 1fr))",
                      gap: "24px",
                    }}
                  >
                    <div
                      style={{
                        background: "white",
                        padding: "20px",
                        borderRadius: "12px",
                      }}
                    >
                      <div style={{ marginBottom: "16px" }}>
                        <p style={{ color: "#4c1d95", margin: 0 }}>
                          Words Allotted
                        </p>
                        <h4 style={{ color: "#1e1b4b", margin: "4px 0 0 0" }}>
                          {selectedUsageMetrics[0].usageMetricsDetails.wordsAllotted.toLocaleString()}
                        </h4>
                      </div>
                      <Progress
                        percent={100}
                        showInfo={false}
                        strokeColor="#7c3aed"
                        trailColor="rgba(124, 58, 237, 0.1)"
                      />
                    </div>

                    <div
                      style={{
                        background: "white",
                        padding: "20px",
                        borderRadius: "12px",
                      }}
                    >
                      <div style={{ marginBottom: "16px" }}>
                        <p style={{ color: "#4c1d95", margin: 0 }}>
                          Words Used
                        </p>
                        <h4 style={{ color: "#1e1b4b", margin: "4px 0 0 0" }}>
                          {selectedUsageMetrics[0].usageMetricsDetails.wordsUsed.toLocaleString()}
                        </h4>
                      </div>
                      <Progress
                        percent={
                          (selectedUsageMetrics[0].usageMetricsDetails
                            .wordsUsed /
                            selectedUsageMetrics[0].usageMetricsDetails
                              .wordsAllotted) *
                          100
                        }
                        showInfo={false}
                        strokeColor="#7c3aed"
                        trailColor="rgba(124, 58, 237, 0.1)"
                      />
                    </div>

                    <div
                      style={{
                        background: "white",
                        padding: "20px",
                        borderRadius: "12px",
                      }}
                    >
                      <div style={{ marginBottom: "16px" }}>
                        <p style={{ color: "#4c1d95", margin: 0 }}>
                          Words Remaining
                        </p>
                        <h4 style={{ color: "#1e1b4b", margin: "4px 0 0 0" }}>
                          {selectedUsageMetrics[0].usageMetricsDetails.wordsLeft.toLocaleString()}
                        </h4>
                      </div>
                      <Progress
                        percent={
                          (selectedUsageMetrics[0].usageMetricsDetails
                            .wordsLeft /
                            selectedUsageMetrics[0].usageMetricsDetails
                              .wordsAllotted) *
                          100
                        }
                        showInfo={false}
                        strokeColor="#7c3aed"
                        trailColor="rgba(124, 58, 237, 0.1)"
                      />
                    </div>
                  </div>
                </motion.div>

                {/* Team Usage */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  style={{
                    background: "rgba(255, 255, 255, 0.5)",
                    borderRadius: "16px",
                    padding: "24px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "24px",
                    }}
                  >
                    <Users size={24} style={{ color: "#7c3aed" }} />
                    <h3 style={{ margin: 0, color: "#1e1b4b" }}>Team Usage</h3>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: "16px",
                    }}
                  >
                    {selectedUsageMetrics.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          background: "white",
                          padding: "20px",
                          borderRadius: "12px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginBottom: "16px",
                          }}
                        >
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "50%",
                              background: "rgba(124, 58, 237, 0.1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#7c3aed",
                              fontWeight: "500",
                              fontSize: "16px",
                            }}
                          >
                            {item?.adminDetails?.name.charAt(0)}
                          </div>
                          <div>
                            <h4 style={{ margin: 0, color: "#1e1b4b" }}>
                              {item?.adminDetails?.name}
                            </h4>
                            <p
                              style={{
                                margin: "4px 0 0 0",
                                color: "#6b7280",
                                fontSize: "14px",
                              }}
                            >
                              Team Member
                            </p>
                          </div>
                        </div>

                        <div style={{ marginBottom: "16px" }}>
                          <p style={{ color: "#4c1d95", margin: "0 0 8px 0" }}>
                            Words Used
                          </p>
                          <Progress
                            percent={
                              (item.adminWordsUsage /
                                selectedUsageMetrics[0].usageMetricsDetails
                                  .wordsUsed) *
                              100
                            }
                            format={(percent) =>
                              `${item.adminWordsUsage.toLocaleString()} words`
                            }
                            strokeColor="#7c3aed"
                            trailColor="rgba(124, 58, 237, 0.1)"
                          />
                        </div>

                        <div>
                          <p style={{ color: "#4c1d95", margin: "0 0 8px 0" }}>
                            Usage Percentage
                          </p>
                          <Progress
                            percent={
                              item.totalWordsUsed !== 0
                                ? (item.adminWordsUsage / item.totalWordsUsed) *
                                  100
                                : 0
                            }
                            strokeColor="#7c3aed"
                            trailColor="rgba(124, 58, 237, 0.1)"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </>
            )}
          </motion.div>
        )}
      </div>
    </>
  );
};

export default TeamAnalytics;
